<script>
import { useAuthStore } from '@/stores/authStore'
import Loading from "@/components/Loading.vue";
import Modal from "@/components/Modal.vue";
import { getCurrentClinic, editClinics } from "@/services/clinicServices"
import { getAllOrgArray } from "@/services/orgServices"
import StatesJSON from "@/constants/states"

export default{
  components: {
    Loading,
    Modal,
  },

  created(){
    this.clinic_id = this.$route.params.id;
    this.get_data(this.clinic_id);
  },

  data() {
    return {
      authStore: useAuthStore(),
      clinic_id: '',
      raw_clinic_data: {},
      clinic_data: {
        clinic_name: "",
        address: "",
        city: "",
        state: "",
        country: "Malaysia",
        postal_code: "",
        latitude: "",
        longitude: "",
        OrganizationUuid: "",
      },
      isLoading: false,
      modal: {
        isSubmitting: false,
      },
      isEditing: false,
      states: StatesJSON,
      orgSelection: [],
      validationError: "",
    }
  },
  methods: {
    async get_data(uuid) {
      this.isLoading = true;
      const result = await getCurrentClinic(uuid);

      if (this.authStore.userRole === 'admin') {
        this.orgSelection = await getAllOrgArray();
      }

      if (result.data) {
        this.raw_clinic_data = result.data;
        this.convert_data(this.raw_clinic_data);
      }
      this.isLoading = false;
    },

    convert_data(input_data){
      this.clinic_data = {
        clinic_name: input_data.ClinicName || "",
        address: input_data.Address || "",
        city: input_data.City || "",
        state: input_data.State || "",
        country: "Malaysia",
        postal_code: input_data.PostalCode || "",
        latitude: input_data.Latitude || "",
        longitude: input_data.Longitude || "",
        OrganizationUuid: input_data.OrganizationUuid || ""
      };
      this.validationError = "";
    },

    close_modal(){
      this.modal.isSubmitting = false;
      this.isEditing = false;
      this.convert_data(this.raw_clinic_data);
    },

    validateForm() {
      if (!this.clinic_data.clinic_name.trim()) {
        this.validationError = "Clinic or hospital name is required.";
        return false;
      }
      if (this.authStore.userRole === 'admin' && !this.clinic_data.OrganizationUuid) {
        this.validationError = "Choose the organization this location belongs to.";
        return false;
      }
      this.validationError = "";
      return true;
    },

    openSubmitModal() {
      if (this.validateForm()) {
        this.modal.isSubmitting = true;
      }
    },

    async submit_edit(){
      this.isLoading = true;
      this.isEditing = false;
      this.modal.isSubmitting = false;
      const result = await editClinics(this.clinic_data, this.clinic_id);
      if(result){
        await this.get_data(this.clinic_id);
      }
      this.isLoading = false;
    }
  }
}
</script>

<template>
  <transition name="fading">
    <Loading v-if="isLoading"/>
  </transition>

  <Modal
    :isVisible="modal.isSubmitting"
    promptText="Save the clinic or hospital changes?"
    yesText="Save"
    noText="Cancel"
    @yes="submit_edit()"
    @no="close_modal()"
  />

  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Location Registry</p>
        <h1 class="page-title">{{ raw_clinic_data.ClinicName || 'Edit location' }}</h1>
        <p class="page-subtitle">Edit the location profile and organization assignment in one place.</p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/clinics')">
        <i class="fas fa-arrow-left"></i> Back to locations
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <section>
          <div class="section-heading">
            <div>
              <h2 class="section-title">Location basics</h2>
              <p class="section-description">Name and parent organization are the important pieces. Address details can be added later.</p>
            </div>
            <span class="badge-soft" :class="{ success: isEditing }">{{ isEditing ? 'Editing' : 'View mode' }}</span>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Clinic / hospital name <span class="required">*</span></label>
              <input class="ui-input" :disabled="!isEditing" v-model="clinic_data.clinic_name">
            </div>

            <div class="form-section" v-if="authStore.userRole === 'admin'">
              <label class="field-label">Organization <span class="required">*</span></label>
              <select class="ui-select" :disabled="!isEditing" v-model="clinic_data.OrganizationUuid">
                <option value="">Choose organization</option>
                <option v-for="org in orgSelection" :key="org.uuid" :value="org.uuid">
                  {{ org.name }}
                </option>
              </select>
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Address</h2>
              <p class="section-description">Optional location details for display, operations, and search.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid">
            <div class="form-section full-width">
              <label class="field-label">Address</label>
              <textarea class="ui-textarea" :disabled="!isEditing" v-model="clinic_data.address"></textarea>
            </div>
            <div class="form-section">
              <label class="field-label">City</label>
              <input class="ui-input" :disabled="!isEditing" v-model="clinic_data.city">
            </div>
            <div class="form-section">
              <label class="field-label">State</label>
              <select class="ui-select" :disabled="!isEditing" v-model="clinic_data.state">
                <option value="">Choose state</option>
                <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Postal code</label>
              <input class="ui-input" :disabled="!isEditing" v-model="clinic_data.postal_code">
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Map coordinates</h2>
              <p class="section-description">Advanced optional values for precise map placement.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Latitude</label>
              <input class="ui-input" :disabled="!isEditing" v-model="clinic_data.latitude">
            </div>
            <div class="form-section">
              <label class="field-label">Longitude</label>
              <input class="ui-input" :disabled="!isEditing" v-model="clinic_data.longitude">
            </div>
          </div>
        </section>

        <div class="ui-actions">
          <button class="ui-button secondary" v-if="!isEditing" @click="$router.push('/clinics')">Back</button>
          <button class="ui-button primary" v-if="!isEditing" @click="isEditing = true">
            <i class="fas fa-edit"></i> Edit location
          </button>
          <button class="ui-button secondary" v-if="isEditing" @click="close_modal()">Cancel</button>
          <button class="ui-button primary" v-if="isEditing" @click="openSubmitModal()">
            <i class="fas fa-save"></i> Save changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
